import { useRef, useEffect, useCallback, useState } from 'react';
import type React from 'react';
import DOMPurify from 'dompurify';
import { fetchWithAuth } from '../lib/api';
import './studio-sections.css';
import type { SelectionInfo } from './BookStudio';

const MEMOIR_ALLOWED_TAGS = [
  'p', 'h1', 'h2', 'h3', 'h4', 'em', 'strong', 'b', 'i', 'br', 'img',
  'a', 'ul', 'ol', 'li', 'blockquote', 'span', 'div', 'section',
  'figure', 'figcaption',
];
const MEMOIR_ALLOWED_ATTR = [
  'data-para-id', 'data-section-id', 'data-excluded', 'contenteditable', 'style', 'class',
  'src', 'alt', 'href', 'id',
];

interface ChapterMeta {
  chapterIndex: number;
  areaId: string;
  title: string;
  sectionId?: string;
}

interface StudioContentProps {
  bookHtml: string;
  restUrl: string;
  chapters: ChapterMeta[];
  lockMap: Record<string, boolean>;
  onSave: (newHtml: string) => void;
  onSelectionChange: (info: SelectionInfo | null) => void;
  onChaptersChange: (chapters: ChapterMeta[]) => void;
  onLockMapChange: (lockMap: Record<string, boolean>) => void;
  /** Populated by StudioContent so BookStudio can route AI-accept calls into the live DOM */
  acceptRewriteRef: React.MutableRefObject<((paraId: string, newText: string) => Promise<void>) | null>;
  /** Called after a chapter section body is successfully saved (Phase 71: marks TOC/Index stale) */
  onChapterBodySaved?: () => void;
}

/**
 * Inject data-para-id attributes onto <p> and <h2> elements using chapterIndex-paragraphIndex
 * scheme. Chapter index increments on each <h2>. Paragraph index resets per chapter.
 */
function injectParagraphIds(html: string): string {
  let chapterIndex = -1;
  let paraIndex = 0;

  return html.replace(/<(p|h2)(\s[^>]*)?>/gi, (_match, tag: string, attrs: string | undefined) => {
    const existingAttrs = attrs ?? '';
    if (tag.toLowerCase() === 'h2') {
      chapterIndex++;
      paraIndex = 0;
      if (/data-para-id=/i.test(existingAttrs)) {
        paraIndex++; // advance so subsequent paragraphs get correct indices
        return _match;
      }
      const id = `${chapterIndex}-${paraIndex++}`;
      return `<${tag}${existingAttrs} data-para-id="${id}" id="chapter-${chapterIndex + 1}">`;
    }
    if (chapterIndex < 0) chapterIndex = 0;
    if (/data-para-id=/i.test(existingAttrs)) {
      paraIndex++;
      return _match;
    }
    const id = `${chapterIndex}-${paraIndex++}`;
    return `<${tag}${existingAttrs} data-para-id="${id}">`;
  });
}

export function StudioContent({
  bookHtml,
  restUrl,
  chapters,
  lockMap,
  onSave,
  onSelectionChange,
  onChaptersChange,
  onLockMapChange,
  acceptRewriteRef,
  onChapterBodySaved,
}: StudioContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const originalContentRef = useRef<Record<string, string>>({});
  const inFlightRef = useRef<Set<string>>(new Set());
  const aliveRef = useRef(true);
  const presentSectionsRef = useRef<ChapterMeta[]>([]);
  // Maps chapterIndex → sectionId; populated from DOM data-section-id attrs after HTML render.
  const sectionIdMapRef = useRef<Map<number, string>>(new Map());
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // WR-02: lockToastTimerRef — infrastructure for future lock-toast dismiss timer.
  // No bare lock toast setTimeout exists yet; ref + cleanup prevent leaks when added.
  const lockToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wire up the AI-accept handler into the live DOM
  useEffect(() => {
    acceptRewriteRef.current = async (paraId: string, newText: string) => {
      const el = contentRef.current?.querySelector<HTMLElement>(`[data-para-id="${paraId}"]`);
      if (!el) return;
      const original = el.textContent ?? '';
      el.textContent = newText;
      const updatedHtml = contentRef.current!.innerHTML;
      onSave(updatedHtml);
      try {
        // Route to per-section API if data-section-id is present (Phase 68 D-05)
        const sectionContainer = el.closest<HTMLElement>('[data-section-id]');
        const sectionId = sectionContainer?.getAttribute('data-section-id');
        let res: Response;
        if (sectionId) {
          const isTitle = el.tagName === 'H2';
          res = await fetchWithAuth(`${restUrl}book/section/${sectionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isTitle ? { title: el.textContent } : { body: sectionContainer!.innerHTML }),
          });
        } else {
          // Fallback: pre-Phase-67 full HTML save
          res = await fetchWithAuth(`${restUrl}book/html`, {
            method: 'PUT',
            headers: { 'Content-Type': 'text/html; charset=utf-8' },
            body: updatedHtml,
          });
        }
        if (!res.ok) throw new Error('Save failed');
      } catch {
        el.textContent = original;
        onSave(contentRef.current!.innerHTML);
        setSaveError('Changes could not be saved. Please try again.');
      }
    };
    return () => { acceptRewriteRef.current = null; };
  }, [restUrl, onSave, acceptRewriteRef]);

  // Dismiss error after 8s
  useEffect(() => {
    if (!saveError) return;
    if (saveErrorTimerRef.current) clearTimeout(saveErrorTimerRef.current);
    saveErrorTimerRef.current = setTimeout(() => setSaveError(null), 8000);
    return () => {
      if (saveErrorTimerRef.current) clearTimeout(saveErrorTimerRef.current);
    };
  }, [saveError]);

  // WR-02: Clean up lockToastTimerRef on unmount to prevent timer leak.
  useEffect(() => {
    return () => {
      if (lockToastTimerRef.current) clearTimeout(lockToastTimerRef.current);
    };
  }, []);

  // Fetch chapters and lock state on mount
  useEffect(() => {
    if (!restUrl) return;
    Promise.all([
      fetchWithAuth(`${restUrl}chapters`).then(r => r.json()),
      fetchWithAuth(`${restUrl}chapter-locks`).then(r => r.json()),
    ]).then(([chapData, lockData]) => {
      const chaps = (
        (chapData as { chapters: { chapterIndex: number; areaId?: string; title: string }[] }).chapters ?? []
      )
        .filter(c => c.areaId)
        .map(c => ({
          chapterIndex: c.chapterIndex,
          areaId: c.areaId!,
          title: c.title,
          sectionId: sectionIdMapRef.current.get(c.chapterIndex),
        }))
        .sort((a, b) => a.chapterIndex - b.chapterIndex);
      onChaptersChange(chaps);

      const locks: Record<string, boolean> = {};
      for (const { areaId, locked } of (
        lockData as { locks: { areaId: string; locked: boolean }[] }
      ).locks ?? []) {
        locks[areaId] = locked;
      }
      onLockMapChange(locks);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restUrl]);

  // Render HTML and set up contenteditable elements
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    // Reset alive flag on every effect run — cleanup sets it to false when
    // bookHtml changes; without this reset, the .catch() handler in blur
    // is permanently suppressed after the first edit cycle (WR-01).
    aliveRef.current = true;

    const sanitized = DOMPurify.sanitize(bookHtml, {
      ALLOWED_TAGS: MEMOIR_ALLOWED_TAGS,
      ALLOWED_ATTR: MEMOIR_ALLOWED_ATTR,
    });
    const processed = injectParagraphIds(sanitized);
    container.innerHTML = processed;

    // Build chapterIndex → sectionId map from data-section-id wrappers (Phase 68 D-08).
    sectionIdMapRef.current.clear();
    container.querySelectorAll<HTMLElement>('[data-section-id]').forEach(sectionEl => {
      const sectionId = sectionEl.getAttribute('data-section-id');
      if (!sectionId) return;
      const h2 = sectionEl.querySelector<HTMLElement>('h2[id^="chapter-"]');
      if (!h2) return;
      const m = (h2.getAttribute('id') ?? '').match(/^chapter-(\d+)$/);
      if (m) sectionIdMapRef.current.set(Number(m[1]) - 1, sectionId);
    });

    // ─── Front/back matter handling (D-06, D-09, D-10, D-11) ──────────────
    type SectionKind = 'title-page' | 'dedication-page' | 'toc-page' | 'index-page' | 'back-matter';
    const SECTION_META: Record<SectionKind, { chapterIndex: number; title: string }> = {
      'title-page':      { chapterIndex: -3, title: 'Title Page'       },
      'dedication-page': { chapterIndex: -2, title: 'Dedication'        },
      'toc-page':        { chapterIndex: -1, title: 'Contents'          },
      'index-page':      { chapterIndex: 999, title: 'Index'            },
      'back-matter':     { chapterIndex: 998, title: 'About the Author' },
    };

    const presentSections: ChapterMeta[] = [];

    // Build save handler for structured-section edits (routes to PUT /book/section/:id)
    const makeStructuredSaveHandler = (sectionEl: HTMLElement, editableEl: HTMLElement) => () => {
      const sectionId = sectionEl.getAttribute('data-section-id');
      if (!sectionId) return;
      const body = DOMPurify.sanitize(editableEl.innerHTML, {
        ALLOWED_TAGS: MEMOIR_ALLOWED_TAGS,
        ALLOWED_ATTR: MEMOIR_ALLOWED_ATTR,
      });
      fetchWithAuth(`${restUrl}book/section/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      }).catch(() => {
        if (!aliveRef.current) return;
        setSaveError('Changes could not be saved. Please try again.');
      });
    };

    // Shared save handler for legacy section-level edits (index, etc.)
    const saveSectionEdit = () => {
      const updatedHtml = container.innerHTML;
      onSave(updatedHtml);
      fetchWithAuth(`${restUrl}book/html`, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/html; charset=utf-8' },
        body: updatedHtml,
      }).catch(() => {
        if (!aliveRef.current) return;
        setSaveError('Changes could not be saved. Please try again.');
      });
    };

    (Object.keys(SECTION_META) as SectionKind[]).forEach(kind => {
      const sectionEl = container.querySelector<HTMLElement>(`section.${kind}`);
      if (!sectionEl) return;

      // Inject ID for sidebar scrollIntoView (D-09).
      sectionEl.id = `section-${kind}`;

      // Populate sectionIdMapRef for front/back matter using sentinel chapterIndex values
      const sectionId = sectionEl.getAttribute('data-section-id');
      if (sectionId) {
        sectionIdMapRef.current.set(SECTION_META[kind].chapterIndex, sectionId);
      }

      if (kind === 'dedication-page') {
        sectionEl.contentEditable = 'false';
        const textEl = sectionEl.querySelector<HTMLElement>('.dedication-text');
        if (textEl) {
          textEl.contentEditable = 'true';
          textEl.style.outline = 'none';
          const handler = sectionId ? makeStructuredSaveHandler(sectionEl, textEl) : saveSectionEdit;
          textEl.addEventListener('blur', handler);
        }
      } else if (kind === 'back-matter') {
        sectionEl.contentEditable = 'false';
        const contentEl = sectionEl.querySelector<HTMLElement>('.back-matter-content');
        if (contentEl) {
          // Show generating indicator if empty
          if (!contentEl.textContent?.trim()) {
            contentEl.innerHTML = '<p class="generating-indicator">Generating content…</p>';
          } else {
            contentEl.contentEditable = 'true';
            contentEl.style.outline = 'none';
            const handler = sectionId ? makeStructuredSaveHandler(sectionEl, contentEl) : saveSectionEdit;
            contentEl.addEventListener('blur', handler);
          }
        }
      } else if (kind === 'index-page') {
        sectionEl.contentEditable = 'false';
        const contentEl = sectionEl.querySelector<HTMLElement>('.index-content');
        if (contentEl) {
          contentEl.contentEditable = 'true';
          contentEl.style.outline = 'none';
          contentEl.addEventListener('blur', saveSectionEdit);
        }
      } else {
        // title-page and toc-page: fully read-only
        sectionEl.contentEditable = 'false';
        sectionEl.style.cursor = 'default';
        sectionEl.querySelectorAll<HTMLElement>('*').forEach(child => {
          child.contentEditable = 'false';
          child.style.cursor = 'default';
        });
      }

      // Build synthetic ChapterMeta (D-10).
      presentSections.push({
        chapterIndex: SECTION_META[kind].chapterIndex,
        areaId: '',
        title: SECTION_META[kind].title,
        sectionId: sectionId ?? undefined,
      });
    });

    // Make all data-para-id elements contenteditable
    const editables = container.querySelectorAll<HTMLElement>('[data-para-id]');
    editables.forEach(el => {
      el.contentEditable = 'true';
      el.style.outline = 'none';

      const paraId = el.getAttribute('data-para-id') ?? '';

      el.addEventListener('focus', () => {
        originalContentRef.current[paraId] = el.textContent ?? '';
        el.style.boxShadow = '0 0 0 2px #f59e0b, 0 0 0 4px rgba(245,158,11,0.2)';
        el.style.borderRadius = '2px';
      });

      el.addEventListener('blur', () => {
        el.style.boxShadow = '';
        el.style.borderRadius = '';

        const newContent = el.textContent ?? '';
        const original = originalContentRef.current[paraId];

        if (newContent === original) return;

        // Route to per-section API if data-section-id is present (Phase 68 D-05)
        const sectionContainer = el.closest<HTMLElement>('[data-section-id]');
        const sectionId = sectionContainer?.getAttribute('data-section-id');

        // Use sectionId as guard key for section-level saves; paraId for legacy fallback
        const guardKey = sectionId ?? paraId;
        inFlightRef.current.add(guardKey);

        const updatedHtml = container.innerHTML;
        onSave(updatedHtml);

        let savePromise: Promise<Response>;
        if (sectionId) {
          const isTitle = el.tagName === 'H2';
          savePromise = fetchWithAuth(`${restUrl}book/section/${sectionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isTitle ? { title: el.textContent } : { body: sectionContainer!.innerHTML }),
          });
        } else {
          // Fallback: pre-Phase-67 full HTML save
          savePromise = fetchWithAuth(`${restUrl}book/html`, {
            method: 'PUT',
            headers: { 'Content-Type': 'text/html; charset=utf-8' },
            body: updatedHtml,
          });
        }

        savePromise
          .then(res => {
            if (!res.ok) throw new Error(`Save failed: ${res.status}`);
            // Notify BookStudio that a chapter body was saved (marks TOC/Index stale)
            if (sectionId) onChapterBodySaved?.();
          })
          .catch(() => {
            if (!aliveRef.current) return;
            if (!inFlightRef.current.has(guardKey)) return;
            setSaveError('Changes could not be saved. Please try again.');
            el.textContent = original ?? '';
            onSave(container.innerHTML);
          })
          .finally(() => {
            inFlightRef.current.delete(guardKey);
          });
      });
    });

    // Store discovered front/back matter sections in a ref. The actual merge
    // with real chapters (from the async fetch) happens in a separate effect
    // that depends on `chapters`, so it fires after the fetch resolves (CR-03).
    presentSectionsRef.current = presentSections;

    return () => {
      aliveRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookHtml, restUrl, onSave]);

  // Merge front/back matter synthetic entries with real chapters once the API
  // fetch lands. The idempotency guard (hasFrontBack) prevents re-firing after
  // the first merge — breaks the onChaptersChange → chapters-prop → effect loop.
  // Also backfills sectionId from the DOM map in case the fetch beat the HTML render.
  useEffect(() => {
    if (presentSectionsRef.current.length === 0) return;
    const hasFrontBack = chapters.some(c => c.areaId === '');
    if (hasFrontBack) return;
    const merged = [
      ...presentSectionsRef.current,
      ...chapters.map(c => ({
        ...c,
        sectionId: c.sectionId ?? sectionIdMapRef.current.get(c.chapterIndex),
      })),
    ].sort((a, b) => a.chapterIndex - b.chapterIndex);
    onChaptersChange(merged);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters]);

  // Enforce chapter locks at the DOM level (D-02, WR-02). Re-runs after every
  // HTML render so freshly-injected paragraphs pick up the correct lock state.
  // Using contentEditable=false prevents typing entirely — no need to also
  // guard the blur handler (D-03).
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const editables = container.querySelectorAll<HTMLElement>('[data-para-id]');
    editables.forEach(el => {
      const paraId = el.getAttribute('data-para-id') ?? '';
      const chapterIndexStr = paraId.split('-')[0];
      const chapterIndex = Number(chapterIndexStr);
      if (Number.isNaN(chapterIndex)) return;

      const areaId = chapters.find(c => c.chapterIndex === chapterIndex)?.areaId ?? '';
      // areaId === '' means front/back matter (no real chapter) — leave whatever
      // the render effect / Plan 2 set (read-only there). Do not override.
      if (!areaId) return;

      const locked = !!lockMap[areaId];
      el.contentEditable = locked ? 'false' : 'true';
      el.style.cursor = locked ? 'default' : 'text';
    });
  }, [chapters, lockMap, bookHtml]);

  // Selection detection — mouseup and touchend
  const handleSelectionEvent = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.rangeCount) {
      onSelectionChange(null);
      return;
    }

    const anchorPara = selection.anchorNode?.parentElement?.closest<HTMLElement>('[data-para-id]');
    const focusPara = selection.focusNode?.parentElement?.closest<HTMLElement>('[data-para-id]');

    if (!anchorPara || !focusPara || anchorPara !== focusPara) {
      onSelectionChange(null);
      return;
    }

    const paraId = anchorPara.getAttribute('data-para-id');
    if (!paraId) { onSelectionChange(null); return; }

    const chapterIndex = Number(paraId.split('-')[0]);
    const areaId = chapters.find(c => c.chapterIndex === chapterIndex)?.areaId ?? '';
    const chapterLabel = `Chapter ${chapterIndex + 1}, paragraph ${paraId.split('-')[1] ?? ''}`;

    onSelectionChange({
      paraId,
      text: anchorPara.textContent?.trim() ?? '',
      areaId,
      chapterLabel,
    });
  }, [chapters, onSelectionChange]);

  return (
    <div className="relative">
      {/* Save error banner */}
      {saveError && (
        <div
          role="alert"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg shadow-md"
        >
          {saveError}
        </div>
      )}

      {/* Prose content pane */}
      <div
        ref={contentRef}
        className="max-w-[700px] mx-auto px-8 py-16"
        style={{ fontFamily: "'Libre Baskerville', 'EB Garamond', Georgia, serif", fontSize: '1rem', lineHeight: '1.75', color: '#1a1a1a' }}
        onMouseUp={handleSelectionEvent}
        onTouchEnd={handleSelectionEvent}
        aria-label="Book content — click any paragraph to edit"
      />
    </div>
  );
}
